/**
 * A product campaign's automation plan (product page › Campaigns): what runs on its Phrase, Broad
 * and Exact campaigns, and from which day. Phrase and Broad (with Shopping) feed: they negate what
 * does not convert, harvest what does into Exact and then negate it at home, and find new keywords.
 * Exact is the main campaign: its bids move in steps, its slow keywords pause and come back for a
 * retest, and its ad is rewritten from what converts. Each item runs from its start date (null: off
 * for that campaign). An item is one or more built-in rules, or a scheduled job.
 */

export type PlanRole = "exact" | "phrase" | "broad";
export const PLAN_ROLES: PlanRole[] = ["phrase", "broad", "exact"];

export type PlanItem =
  | "negate"
  | "irrelevant"
  | "harvest"
  | "promote"
  | "broaden"
  | "discover"
  | "bids"
  | "pause_retest"
  | "refresh_ads";

export interface PlanItemDef {
  item: PlanItem;
  label: string;
  detail: string;
  roles: PlanRole[];
  /** the built-in rules the item switches (gated per campaign) */
  rules: string[];
  /** a scheduled job instead of rules */
  job: boolean;
  /** days after the campaign is created that it starts by default; null = off until asked for */
  defaultAfterDays: number | null;
  /** without a plan: "workspace" = the rules act as set in Automations; "off" = it runs nowhere */
  withoutPlan: "workspace" | "off";
}

export const PLAN_ITEMS: PlanItemDef[] = [
  {
    item: "negate",
    label: "Negate searches that don't convert",
    detail:
      "Enough clicks to expect an order, none came and the cost passed max CPA → exact negative. Competitor searches without an order too.",
    roles: ["phrase", "broad"],
    rules: ["negate_nonconverting_terms", "competitor_guard"],
    job: false,
    defaultAfterDays: 0,
    withoutPlan: "workspace",
  },
  {
    item: "irrelevant",
    label: "Negate irrelevant words (AI)",
    detail:
      "The AI reads the campaign's searches; a word it marks irrelevant that spent without an order becomes a phrase negative.",
    roles: ["phrase", "broad"],
    rules: ["negate_irrelevant_words"],
    job: false,
    defaultAfterDays: 0,
    withoutPlan: "off",
  },
  {
    item: "harvest",
    label: "Harvest to Exact, then negate here",
    detail:
      "A search with 2+ orders under max CPA becomes an Exact keyword in the product's Exact campaign and an exact negative here, so the two do not compete.",
    roles: ["phrase", "broad"],
    rules: ["harvest_proven_terms"],
    job: false,
    defaultAfterDays: 0,
    withoutPlan: "workspace",
  },
  {
    item: "promote",
    label: "Promising searches to Phrase",
    detail: "1 order under max CPA, or 10+ clicks with a strong CTR → Phrase keyword in the Phrase campaign.",
    roles: ["phrase", "broad"],
    rules: ["promote_promising_terms"],
    job: false,
    defaultAfterDays: 0,
    withoutPlan: "workspace",
  },
  {
    item: "broaden",
    label: "Converting Phrase keywords to Broad",
    detail: "A Phrase keyword with an order under max CPA → the same keyword in the Broad campaign.",
    roles: ["phrase"],
    rules: ["broaden_converting_phrase"],
    job: false,
    defaultAfterDays: 0,
    withoutPlan: "workspace",
  },
  {
    item: "discover",
    label: "Find new keywords every week",
    detail:
      "Keyword Planner and AI ideas that pass the fit check with 50+ searches a month are added: up to 20 a week to Phrase, up to 5 short ones to Broad.",
    roles: ["phrase", "broad"],
    rules: [],
    job: true,
    defaultAfterDays: 0,
    withoutPlan: "off",
  },
  {
    item: "bids",
    label: "Bids up 10% / down 15%",
    detail:
      "Manual CPC keywords: 2+ orders at ≤ 70% of max CPA → +10% max CPC (never above max CPA); cost per order over max CPA → −15%. Weekly at most.",
    roles: ["exact"],
    rules: ["exact_bid_up", "exact_bid_down"],
    job: false,
    defaultAfterDays: 14,
    withoutPlan: "off",
  },
  {
    item: "pause_retest",
    label: "Pause slow keywords, retest after 30 days",
    detail:
      "A keyword with enough clicks to expect an order, none, and cost past max CPA is paused; 30 days later it is switched back on to try again.",
    roles: ["exact"],
    rules: ["exact_pause_slow", "exact_retest_paused"],
    job: false,
    defaultAfterDays: 0,
    withoutPlan: "off",
  },
  {
    item: "refresh_ads",
    label: "Rewrite ad headlines & descriptions",
    detail:
      "Every 30 days the AI writes a new ad from the best-converting keywords, keeping the lines Google rates Best or Good; the new ad goes live and the old one is paused.",
    roles: ["exact"],
    rules: [],
    job: true,
    defaultAfterDays: null,
    withoutPlan: "off",
  },
];

/** rule id → the plan item that gates it. */
export const PLAN_RULE_ITEM: Record<string, PlanItem> = Object.fromEntries(
  PLAN_ITEMS.flatMap((d) => d.rules.map((r) => [r, d.item])),
);

export const planItemsFor = (role: PlanRole) => PLAN_ITEMS.filter((d) => d.roles.includes(role));

/**
 * Rules that feed Exact (negate, irrelevant words, harvest, promote, broaden): they never act on
 * an Exact campaign (or the Harvest campaign), whatever campaigns the rule is set to.
 */
export const FEEDER_RULES = new Set(
  PLAN_ITEMS.filter((d) => !d.roles.includes("exact")).flatMap((d) => d.rules),
);

/** One item of a campaign's plan as the client sends it: start after N days (0 = now), or off. */
export interface PlanChoice {
  item: PlanItem;
  afterDays: number | null;
}

export interface CampaignPlanItem {
  item: PlanItem;
  /** account-local date it starts on; null = off for this campaign */
  startsOn: string | null;
  /** a rule it needs is switched off for the whole workspace in Automations */
  ruleOff: boolean;
  /** jobs: when it last acted */
  lastRunAt: string | null;
}

export interface CampaignPlanRole {
  role: PlanRole;
  /** the product's live campaign of this role, if any */
  campaign: { key: string; name: string; status: string } | null;
  /** a plan is saved; without one the workspace's rule settings apply */
  saved: boolean;
  items: CampaignPlanItem[];
}

/** GET /products/:id/campaign-automation */
export interface CampaignPlanResponse {
  /** account-local today */
  today: string;
  roles: CampaignPlanRole[];
}

/** PUT /products/:id/campaign-automation; afterDays omitted = off */
export interface SaveCampaignPlanBody {
  role: PlanRole;
  items: Array<{ item: PlanItem; afterDays?: number }>;
}

/** A plan choice as the API takes it: "off" is sent without afterDays. */
export const planChoiceBody = (item: string, afterDays: number | null) =>
  afterDays === null ? { item } : { item, afterDays };
